import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Table } from '@quintoandar-tokko/jopijs';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import parentStyles from '../dashboardTables.module.scss';
import specificStyles from './propertyConsultations.module.scss';
import LoadingMoreSkeleton from '../components/LoadingMoreSkeleton';
import { getPerformancePropertyConsultationsTable } from '@/store/slices/components/dashboard/service';
import buildQuery from '../../utils/buildQuery';
import useObserver from '@/hooks/useObserver';
import { setPerformanceModals } from '@/store/slices/components/dashboard/DashboardSlice';
import HeaderCell from '../components/HeaderCell';
import buildTableQuery from '../../utils/buildTableQuery';
import { TABLE_SORT_FIELDS } from '../../constants';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';
import PropertyConsultationsTableRow from './PropertyConsultationsTableRow';

export default function PropertyConsultationsTable() {
  const dispatch = useDispatch();
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { propertyConsultations } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const { ref: loadMoreRef, isInViewport } = useObserver();
  const track = useTrackPerformanceTable();

  const headers = [
    {
      id: 1,
      title: Trans(translations, 'Property'),
      subtitle: Trans(translations, 'Location'),
      field: TABLE_SORT_FIELDS.PROPERTY_ADDRESS,
    },
    {
      id: 2,
      title: Trans(translations, 'Operation'),
      field: TABLE_SORT_FIELDS.OPERATION,
    },
    {
      id: 3,
      title: Trans(translations, 'PropertyType'),
      field: TABLE_SORT_FIELDS.PROPERTY_TYPE,
    },
    {
      id: 4,
      title: Trans(translations, 'Value'),
      field: TABLE_SORT_FIELDS.VALUE,
    },
    {
      id: 5,
      title: Trans(translations, 'Propietary'),
      field: TABLE_SORT_FIELDS.OWNER_NAME,
    },
    {
      id: 6,
      title: Trans(translations, 'Producer'),
      field: TABLE_SORT_FIELDS.AGENT_NAME,
    },
    {
      id: 7,
      title: Trans(translations, 'Inquiries'),
      field: TABLE_SORT_FIELDS.INQUIRIES,
    },
  ];

  useEffect(() => {
    if (!propertyConsultations.has_next) return;
    if (!isInViewport || propertyConsultations.loading) return;

    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(propertyConsultations, {
      withNextPage: true,
    });

    dispatch(getPerformancePropertyConsultationsTable({ query, tableQuery }));
  }, [isInViewport]);

  const handleCellClick = (header) => {
    dispatch(
      setPerformanceModals({
        key: 'propertyConsultations',
        subkey: 'sortBy',
        value: header.field,
      })
    );
    track.propertyConsultations(TRACK_ELEMENT.COLUMN_ORDER);
  };

  return (
    <Table
      id="property-consultations-table"
      className={`${parentStyles.table} ${specificStyles.propertyConsultationsTable}`}
    >
      <Table.Header className={parentStyles.tableHeader}>
        {headers.map((header) => (
          <HeaderCell
            key={header.id}
            id={header.id}
            title={header.title}
            subtitle={header.subtitle}
            field={header.field}
            sortBy={propertyConsultations.sortBy}
            sortDirection={propertyConsultations.sortDirection}
            onClick={() => handleCellClick(header)}
          />
        ))}
      </Table.Header>

      <Table.Rows>
        {propertyConsultations.list.map((row) => (
          <PropertyConsultationsTableRow key={row.id} row={row} />
        ))}

        <Box as="tr" ref={loadMoreRef} style={{ height: 1, opacity: 0 }} />

        {(propertyConsultations.loading || propertyConsultations.has_next) && (
          <LoadingMoreSkeleton columns={headers.length} />
        )}
      </Table.Rows>
    </Table>
  );
}
